import { useState, FormEvent } from 'react';
import type { User, CreateUserRequest, Outlet } from '@photobooth/types';

interface UserFormProps {
  user?: User;
  outlets: Outlet[];
  onSubmit: (data: CreateUserRequest) => Promise<void>;
  onCancel: () => void;
  /**
   * When set to 'adminOnly', this form will:
   * - Fix the role to 'admin'
   * - Hide role and outlet selection fields
   */
  mode?: 'default' | 'adminOnly';
}

export function UserForm({ user, outlets, onSubmit, onCancel, mode = 'default' }: UserFormProps) {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    password: '',
    role: mode === 'adminOnly' ? 'admin' : user?.role || 'cashier',
    outletIds: user?.outletId ? [user.outletId] : ([] as string[]),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [outletSearch, setOutletSearch] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const role = 'admin' as const;
      const outletIds = mode === 'adminOnly' ? undefined : (formData.outletIds.length > 0 ? formData.outletIds : undefined);
      const outletId = mode === 'adminOnly' ? undefined : formData.outletIds[0] || undefined;

      const data: CreateUserRequest = {
        username: formData.username,
        password: formData.password,
        role: mode === 'adminOnly' ? role : (formData.role as 'cashier' | 'admin'),
        outletId,
        outletIds,
      };

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleOutlet = (outletId: string) => {
    setFormData(prev => ({
      ...prev,
      outletIds: prev.outletIds.includes(outletId)
        ? prev.outletIds.filter(id => id !== outletId)
        : [...prev.outletIds, outletId]
    }));
  };

  const filteredOutlets = outletSearch.trim()
    ? outlets.filter(
        (o) =>
          o.name.toLowerCase().includes(outletSearch.toLowerCase()) ||
          o.location.toLowerCase().includes(outletSearch.toLowerCase()) ||
          (o.slug && o.slug.toLowerCase().includes(outletSearch.toLowerCase()))
      )
    : outlets;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="username" className="block text-sm font-medium text-text mb-1">
          Username *
        </label>
        <input
          id="username"
          type="text"
          required
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="john.doe"
        />
      </div>

      {!user ? (
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
            Password * <span className="text-xs text-text-muted">(minimum 6 characters)</span>
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={6}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="••••••••"
          />
          <p className="text-xs text-text-muted mt-1">
            Password must be at least 6 characters long
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
            Change password <span className="text-xs text-text-muted">(leave blank to keep current)</span>
          </label>
          <input
            id="password"
            type="password"
            minLength={6}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="••••••••"
          />
        </div>
      )}

      {mode !== 'adminOnly' && (
        <>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-text mb-1">
              Role *
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value as typeof formData.role })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="cashier">Cashier</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-2">
              Outlets <span className="text-xs text-text-muted">(select one or more)</span>
            </label>
            <input
              type="text"
              value={outletSearch}
              onChange={(e) => setOutletSearch(e.target.value)}
              placeholder="Search by name, location, or slug..."
              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
            <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
              {outlets.length === 0 ? (
                <p className="text-sm text-text-muted">No outlets available</p>
              ) : filteredOutlets.length === 0 ? (
                <p className="text-sm text-text-muted">No outlets match your search</p>
              ) : (
                filteredOutlets.map((outlet) => (
                  <label
                    key={outlet.outletId}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.outletIds.includes(outlet.outletId)}
                      onChange={() => toggleOutlet(outlet.outletId)}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm text-text">{outlet.name}</span>
                    <span className="text-xs text-text-muted">({outlet.location})</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-text-muted mt-1">
              {formData.role === 'admin'
                ? 'Admins can access all outlets by default'
                : formData.role === 'cashier'
                  ? 'Cashiers must have at least one outlet to log in'
                  : 'Select which outlets this user can access'}
            </p>
          </div>
        </>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : user ? 'Update Admin' : 'Create Admin'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-2 px-4 rounded-md hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
